/**
 * AddSignerDialog - Dialog premium para adicionar novo signatário a um contrato
 * Usado no ContractDetailSheet para contratos com status 'generated' ou 'sent'
 * 
 * RELEASE: v1.3.0 - 2026-08-24
 * - v1.3.0: Suporte a MODO EDIÇÃO (mode="edit"): permite corrigir nome/e-mail/telefone/
 *           papel/forma de entrega de signatário pendente. Em contratos já enviados o
 *           signatário é SUBSTITUÍDO na autenticadora (novo link de assinatura).
 * RELEASE: v1.2.0 - 2026-02-19
 * - v1.1.0: PhoneInput com máscara + visual premium
 * - v1.2.0: Lista de papéis importada de @/types/contracts (fonte oficial, 6 papéis corretos)
 *           Métodos de entrega filtrados pelo PublishedContract via prop allowedDeliveryMethods
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { PhoneInput } from '@/components/ui/phone-input';
import { Loader2, User, Mail, Phone, ShieldCheck, UserPlus, Pencil } from 'lucide-react';
import { InlineNotice } from '@/components/ui/inline-notice';
import { showToast } from '@/lib/toast';
// Fonte oficial dos papéis: alinhado com WizardStepSigners e a arquitetura multi-provider
import { SIGNER_ROLES } from '@/types/contracts';

/**
 * Todos os métodos de entrega suportados pela plataforma.
 * Filtrados pela prop allowedDeliveryMethods vinda do PublishedContract.
 */
const ALL_DELIVERY_METHODS = [
  { value: 'email', label: 'E-mail' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'sms', label: 'SMS' },
];

interface AddSignerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    signer_name: string;
    signer_email?: string;
    signer_phone?: string;
    signer_role: string;
    delivery_method: string;
  }) => Promise<void>;
  isLoading?: boolean;
  /** Métodos de entrega permitidos pelo PublishedContract (fallback: todos) */
  allowedDeliveryMethods?: ('email' | 'whatsapp' | 'sms')[];
  /** Modo do formulário: criação (padrão) ou edição de signatário existente */
  mode?: 'create' | 'edit';
  /** Dados iniciais no modo edição */
  initialData?: {
    signer_name?: string | null;
    signer_email?: string | null;
    signer_phone?: string | null;
    signer_role?: string | null;
    delivery_method?: string | null;
  } | null;
  /** Indica que o contrato já foi enviado (substituição na autenticadora) */
  willReplaceOnProvider?: boolean;
}

/**
 * Dialog com formulário para adicionar novo signatário
 * Validação: nome obrigatório + pelo menos email OU telefone
 */
export function AddSignerDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading,
  allowedDeliveryMethods,
  mode = 'create',
  initialData = null,
  willReplaceOnProvider = false,
}: AddSignerDialogProps) {
  const isEdit = mode === 'edit';
  // Filtra os métodos de entrega pelos permitidos no PublishedContract
  // Fallback: exibe email e whatsapp se não houver restrição configurada
  const deliveryMethods = allowedDeliveryMethods && allowedDeliveryMethods.length > 0
    ? ALL_DELIVERY_METHODS.filter(m => allowedDeliveryMethods.includes(m.value as 'email' | 'whatsapp' | 'sms'))
    : ALL_DELIVERY_METHODS.filter(m => ['email', 'whatsapp'].includes(m.value));

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('contratado');
  // Inicializa com o primeiro método permitido
  const [deliveryMethod, setDeliveryMethod] = useState(() => deliveryMethods[0]?.value ?? 'email');

  // No modo edição, pré-carrega os dados atuais do signatário sempre que abrir
  useEffect(() => {
    if (open && isEdit && initialData) {
      setName(initialData.signer_name ?? '');
      setEmail(initialData.signer_email ?? '');
      setPhone(initialData.signer_phone ?? '');
      setRole(initialData.signer_role || 'contratado');
      setDeliveryMethod(initialData.delivery_method || deliveryMethods[0]?.value || 'email');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, initialData]);

  /** Reseta formulário ao fechar */
  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setName('');
      setEmail('');
      setPhone('');
      setRole('contratado');
      setDeliveryMethod('email');
    }
    onOpenChange(value);
  };

  /** Submete dados do novo signatário */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validação: nome obrigatório
    if (!name.trim()) {
      showToast.error('Nome obrigatório', 'Informe o nome do signatário.');
      return;
    }

    // Validação: pelo menos email ou telefone
    if (!email.trim() && !phone.trim()) {
      showToast.error('Contato obrigatório', 'Informe pelo menos o e-mail ou telefone.');
      return;
    }

    await onSubmit({
      signer_name: name.trim(),
      signer_email: email.trim() || undefined,
      signer_phone: phone.trim() || undefined,
      signer_role: role,
      delivery_method: deliveryMethod,
    });

    // Fecha dialog após sucesso
    handleOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEdit ? <Pencil className="h-5 w-5 text-primary" /> : <UserPlus className="h-5 w-5 text-primary" />}
            {isEdit ? 'Editar Signatário' : 'Adicionar Signatário'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Corrija os dados de contato do signatário pendente.'
              : 'Preencha os dados do novo participante do contrato.'}
          </DialogDescription>
        </DialogHeader>

        {/* Avisa que a correção gera novo convite/link na autenticadora */}
        {isEdit && willReplaceOnProvider && (
          <InlineNotice variant="warning">
            O contrato já foi enviado: ao salvar, o signatário será substituído na autenticadora e
            um novo link de assinatura será gerado e enviado.
          </InlineNotice>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Nome (obrigatório) */}
          <div className="space-y-2">
            <Label htmlFor="signer-name" className="flex items-center gap-1.5 text-sm">
              <User className="h-3.5 w-3.5 text-muted-foreground" />
              Nome <span className="text-destructive">*</span>
            </Label>
            <Input
              id="signer-name"
              placeholder="Nome completo do signatário"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {/* E-mail */}
          <div className="space-y-2">
            <Label htmlFor="signer-email" className="flex items-center gap-1.5 text-sm">
              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
              E-mail
            </Label>
            <Input
              id="signer-email"
              type="email"
              placeholder="email@exemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {/* Telefone com PhoneInput (seletor de país + máscara) */}
          <div className="space-y-2">
            <Label htmlFor="signer-phone" className="flex items-center gap-1.5 text-sm">
              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
              Telefone
            </Label>
            <PhoneInput
              id="signer-phone"
              value={phone}
              onChange={setPhone}
              disabled={isLoading}
            />
          </div>

          {/* Papel e Método de entrega lado a lado */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5 text-sm">
                <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                Papel
              </Label>
              <Select value={role} onValueChange={setRole} disabled={isLoading}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SIGNER_ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Entrega via</Label>
              <Select value={deliveryMethod} onValueChange={setDeliveryMethod} disabled={isLoading}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {deliveryMethods.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading} className="gap-1.5">
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? <Pencil className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
              {isEdit ? 'Salvar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
